export interface PieceTheme {
  readonly id: string;
  readonly name: string;
  /** Material color for white pieces. */
  readonly white: string;
  /** Material color for black pieces. */
  readonly black: string;
  /** Generated preview sheet (app/public/themes/pieces/<id>.webp). */
  readonly preview: string;
}

export interface BoardTheme {
  readonly id: string;
  readonly name: string;
  /** Material color for light squares. */
  readonly light: string;
  /** Material color for dark squares. */
  readonly dark: string;
  /** Generated preview tile (app/public/themes/boards/<id>.webp). */
  readonly preview: string;
}

/**
 * Surface underneath the board. Material + tone are independent axes so the
 * player can combine e.g. `wood` + `dark` (oak dining table) or `stone` +
 * `light` (white marble). The registry is consumed by `<Table>` via a
 * `theme: TableTheme` prop; the app derives a `TableTheme` from the persisted
 * `tableMaterialId` + `tableToneId` pair on `useThemeStore`.
 */
export interface TableTheme {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly roughness: number;
  readonly metalness: number;
}

export const TABLE_THEMES: readonly TableTheme[] = [
  // wood — warm tan, low specular, slightly diffuse highlight
  { id: 'wood-light', name: 'Wood — Light', color: '#d4b896', roughness: 0.65, metalness: 0.0 },
  { id: 'wood-dark', name: 'Wood — Dark', color: '#5a3e22', roughness: 0.65, metalness: 0.0 },
  // stone — pale limestone / charcoal slate, fully diffuse
  { id: 'stone-light', name: 'Stone — Light', color: '#d8d4cc', roughness: 0.95, metalness: 0.0 },
  { id: 'stone-dark', name: 'Stone — Dark', color: '#3a3a3a', roughness: 0.95, metalness: 0.0 },
];

// Registry invariants are guarded at module init so downstream callers can rely on a
// non-empty `TABLE_THEMES` / `PIECE_THEMES` / `BOARD_THEMES` array without non-null asserting.
function requireFirst<T>(arr: readonly T[], name: string): T {
  const first = arr[0];
  if (!first) throw new Error(`@strategy4kids/board-3d: ${name} registry is empty`);
  return first;
}

export const DEFAULT_TABLE_THEME: TableTheme = requireFirst(TABLE_THEMES, 'TABLE_THEMES');

export function tableThemeByMaterial(
  material: 'wood' | 'stone',
  tone: 'light' | 'dark',
): TableTheme {
  const id = `${material}-${tone}`;
  return TABLE_THEMES.find((t) => t.id === id) ?? DEFAULT_TABLE_THEME;
}

export const PIECE_THEMES: readonly PieceTheme[] = [
  // `classic.black` lifted from `#2a2a38` to `#3d3a48` so dark pieces stay
  // legible against the dark scene background (`#1d1b26`); previously the
  // dark pieces melted into the frame in dark mode.
  { id: 'classic', name: 'Classic', white: '#f4efe2', black: '#3d3a48', preview: '/themes/pieces/classic.webp' },
  { id: 'candy', name: 'Candy', white: '#ffffff', black: '#ff7eb6', preview: '/themes/pieces/candy.webp' },
];

export const BOARD_THEMES: readonly BoardTheme[] = [
  { id: 'classic', name: 'Classic', light: '#f0d9b5', dark: '#b58863', preview: '/themes/boards/classic.webp' },
  { id: 'candy', name: 'Candy', light: '#d6f6ff', dark: '#ffc8e0', preview: '/themes/boards/candy.webp' },
];

export const DEFAULT_PIECE_THEME: PieceTheme = requireFirst(PIECE_THEMES, 'PIECE_THEMES');
export const DEFAULT_BOARD_THEME: BoardTheme = requireFirst(BOARD_THEMES, 'BOARD_THEMES');

export function pieceThemeById(id: string): PieceTheme {
  return PIECE_THEMES.find((t) => t.id === id) ?? DEFAULT_PIECE_THEME;
}

export function boardThemeById(id: string): BoardTheme {
  return BOARD_THEMES.find((t) => t.id === id) ?? DEFAULT_BOARD_THEME;
}
